import { graphql } from 'gatsby';
import useStaticFooter from 'hooks/useStaticFooter';
import useStaticHeader from 'hooks/useStaticHeader';
import SEO from 'layouts/Seo';
import { useRef } from 'react';
import { contentStyles, richTextStyles, sidebarStyles } from 'styles/templates/Post.styles';
import { twMerge } from 'tailwind-merge';

import Image from 'molecules/Image';
import ScrollToTop from 'molecules/ScrollToTop';
import Section from 'molecules/Section';
import SocialShare from 'molecules/SocialShare';

import CardDeckCarousel from 'components/CardDeck/variants/Carousel';
import { generateUrl, isResourceCenter } from 'components/Cards/utils/helpers';
import ConversionPanel from 'components/ConversionPanel';
import Footer from 'components/Footer';
import Header from 'components/Header';
import PostHero from 'components/Hero/PostHero';
import SubscribeForm from 'components/Resources/SubscribeForm';
import TableOfContents from 'components/TableOfContents';

import richTextParser from 'utils/richTextParser/richTextParser';

import type { ConversionPanelProps } from 'components/ConversionPanel';
import type { FooterProps } from 'components/Footer';
import type { HeaderProps } from 'components/Header';
import type { PageProps } from 'gatsby';
import type {
  ContentfulLayoutSection,
  ContentfulTemplateBlogPost,
  ContentfulTemplateBlogPostConnection,
  ContentfulTemplateCaseStudy,
  ContentfulTemplateCaseStudyConnection,
  ContentfulTemplateNewsPost,
  ContentfulTemplateNewsPostConnection,
  ContentfulTemplateResourceCenter,
  ContentfulTemplateResourceCenterConnection,
} from 'graphqlTypes';
import type { StaticImage } from 'molecules/Image';
import type { SectionProps } from 'molecules/Section';
import type { FC } from 'react';
import type { RichText } from 'utils/richTextParser/richTextParser';

interface PostDataProps {
  blogPostData: ContentfulTemplateBlogPost;
  recentBlogPosts: ContentfulTemplateBlogPostConnection;
  newsPostData: ContentfulTemplateNewsPost;
  recentNewsPosts: ContentfulTemplateNewsPostConnection;
  caseStudiesPostData: ContentfulTemplateCaseStudy;
  recentCaseStudiesPosts: ContentfulTemplateCaseStudyConnection;
  resourceCenterPostData: ContentfulTemplateResourceCenter;
  recentResourceCenterPosts: ContentfulTemplateResourceCenterConnection;
  conversionPanelSection: ContentfulLayoutSection;
}

type pageContextProps = {
  resourceType: string;
  slug: string;
};

const Post: FC<PageProps<PostDataProps>> = ({
  data: {
    blogPostData,
    caseStudiesPostData,
    newsPostData,
    recentBlogPosts,
    recentCaseStudiesPosts,
    recentNewsPosts,
    recentResourceCenterPosts,
    resourceCenterPostData,
    conversionPanelSection,
  },
}) => {
  const postRef = useRef<HTMLDivElement | null>(null);
  const { header } = useStaticHeader();
  const { footer } = useStaticFooter();

  const post = blogPostData || newsPostData || caseStudiesPostData || resourceCenterPostData;
  const recentPosts = recentBlogPosts || recentNewsPosts || recentCaseStudiesPosts || recentResourceCenterPosts;
  const hasHubspotForm = post?.hubspotForm;

  const pathname = generateUrl({
    __typename: post.__typename,
    slug: post.slug,
    type: isResourceCenter(post) ? post.type : undefined,
  });

  return (
    <div>
      {header && <Header {...(header as HeaderProps)} />}
      <main id="post-listing">
        <PostHero post={post} />
        <Section>
          <div className="relative -translate-y-4" ref={postRef}>
            <ScrollToTop contentRef={postRef} offset={243} />
            <div className={twMerge(contentStyles())}>
              <aside className={twMerge(sidebarStyles())}>
                <div className="hidden h-fit xl:flex">
                  <TableOfContents richText={post.body as RichText} />
                </div>
                {pathname && <SocialShare label="Share" pathname={pathname} />}
                <SubscribeForm
                  formId="03c161b8-e171-4e19-9159-434e89791c99"
                  heading="Sign up for our newsletter"
                  subhead="Get the latests articles, delivered right to your inbox."
                />
              </aside>
              <article className="flex flex-col gap-8">
                <div className="xl:hidden">
                  <TableOfContents richText={post.body as RichText} />
                </div>
                <div className={twMerge(richTextStyles())}>
                  {hasHubspotForm && post?.featuredImage && (
                    <Image
                      image={post?.featuredImage as StaticImage}
                      alt={post?.title || 'Card Image'}
                      className="aspect-video object-cover"
                    />
                  )}
                  {richTextParser(post.body as RichText)}
                </div>
              </article>
            </div>
          </div>
        </Section>
        <div className="flex items-center justify-center overflow-hidden bg-primary-25 px-4 pb-16 pt-12">
          {recentPosts && recentPosts?.nodes?.length > 0 && (
            <CardDeckCarousel
              background="primary25"
              cardDeckHeading={{
                contained: false,
                fullWidth: true,
                alignment: 'Left',
                eyebrow: 'RESOURCES',
                heading: 'The latest articles from Aspire Software',
                headingSize: 'md',
                subheading: 'Practical advice and tools to help you run your field service business.',
              }}
              cards={recentPosts?.nodes.map(node => ({
                theme: 'transparent',
                ...node,
              }))}
            />
          )}
        </div>
        {caseStudiesPostData && (
          <ConversionPanel
            {...(conversionPanelSection['component'] as ConversionPanelProps)}
            {...(conversionPanelSection as SectionProps)}
          />
        )}
      </main>
      {footer && <Footer {...(footer as FooterProps)} background="blue900" padding="small" contained />}
    </div>
  );
};

export const Head: FC<PageProps<PostDataProps>> = ({
  data: { blogPostData, caseStudiesPostData, newsPostData, resourceCenterPostData },
  pageContext,
}) => {
  const post = blogPostData || newsPostData || caseStudiesPostData || resourceCenterPostData;
  const { resourceType, slug } = pageContext as pageContextProps;

  return <SEO {...post.seo} slug={`${resourceType}/${slug}`} />;
};

export default Post;

export const postQuery = graphql`
  query templatePostQuery(
    $slug: String!
    $queryBlog: Boolean!
    $queryNews: Boolean!
    $queryCaseStudy: Boolean!
    $queryResourceCenter: Boolean!
  ) {
    blogPostData: contentfulTemplateBlogPost(slug: { eq: $slug }) @include(if: $queryBlog) {
      ...contentfulTemplateBlogPost
    }
    recentBlogPosts: allContentfulTemplateBlogPost(
      limit: 12
      filter: { slug: { ne: $slug }, contentful_id: { ne: "7zH3lxinG2gi8RCRNJd16N" } }
      sort: { publishDate: DESC }
    ) @include(if: $queryBlog) {
      nodes {
        ...contentfulTemplateBlogPost
      }
    }
    newsPostData: contentfulTemplateNewsPost(slug: { eq: $slug }) @include(if: $queryNews) {
      ...contentfulTemplateNewsPost
    }
    recentNewsPosts: allContentfulTemplateNewsPost(
      limit: 12
      filter: { slug: { ne: $slug }, contentful_id: { ne: "15pLbCaEStbrPaUeknkymZ" } }
      sort: { publishDate: DESC }
    ) @include(if: $queryNews) {
      nodes {
        ...contentfulTemplateNewsPost
      }
    }
    caseStudiesPostData: contentfulTemplateCaseStudy(slug: { eq: $slug }) @include(if: $queryCaseStudy) {
      ...contentfulTemplateCaseStudy
    }
    recentCaseStudiesPosts: allContentfulTemplateCaseStudy(
      limit: 12
      filter: { slug: { ne: $slug }, contentful_id: { ne: "5Agw7ldTBmFXc5dMECy3vx" } }
      sort: { publishDate: DESC }
    ) @include(if: $queryCaseStudy) {
      nodes {
        ...contentfulTemplateCaseStudy
      }
    }
    resourceCenterPostData: contentfulTemplateResourceCenter(slug: { eq: $slug }) @include(if: $queryResourceCenter) {
      ...contentfulTemplateResourceCenter
    }
    recentResourceCenterPosts: allContentfulTemplateResourceCenter(
      limit: 12
      filter: { slug: { ne: $slug }, contentful_id: { ne: "5LDKvFV3gjImx0aCmmfjf6" } }
      sort: { publishDate: DESC }
    ) @include(if: $queryResourceCenter) {
      nodes {
        ...contentfulTemplateResourceCenter
      }
    }
    conversionPanelSection: contentfulLayoutSection(contentful_id: { eq: "5BbFCkgh34Nw6aLWj3m1Xp" }) {
      ...contentfulLayoutSection
    }
  }
`;
